// ─────────────────────────────────────────────────────────────────────────────
// reportExport.js
// Shared "Print Report" (PDF) + "Export CSV" for every report page and the
// single-student dossier. Mirrors the Fee Reports letterhead so all printed
// reports look identical, and the printed tables paginate cleanly across pages
// (repeating column headers, no split rows, page numbers).
//
// Public API:
//   printReport({ title, campus, scopeMeta, summaryCards, sections })
//   exportCsv ({ filename, title, campus, scopeMeta, sections })
//
//   sections: array of either
//     { heading, headers:[...], rows:[[...]], rightCols:[...] }   // a table
//     { heading, pairs:[[label,value], ...] }                     // key/value
// ─────────────────────────────────────────────────────────────────────────────

import { alertDialog } from "../../../utils/dialogs";

const SCHOOL_NAME = "American Lycetuff School";
const SCHOOL_LOGO = "/als-logo.png"; // served from public/

// Campus is derived from the students in the report (each carries campus_name,
// scoped by the backend to the logged-in admin's campus).
export const deriveCampus = (students = []) => {
  const names = [...new Set(students.map(s => s.campus_name || s.campus).filter(Boolean))];
  if (names.length === 1) return names[0];
  if (names.length > 1) return "All Campuses";
  return "";
};

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const timestamp = () => new Date().toLocaleString("en-PK", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

// Colour for status-like cell values, so the printed report reads at a glance.
const STATUS_COLOR = {
  Active: "#2e7d32", Paid: "#2e7d32", Graduated: "#1a4b8c", Waived: "#7c3aed",
  Partial: "#8a6c1f", Inactive: "#5a5e6a", Withdrawn: "#c41e24", Unpaid: "#c41e24", "Not Paid": "#c41e24",
};

// ── HTML fragment builders ────────────────────────────────────────────────────
const cardsHtml = (cards = []) => cards && cards.length
  ? `<div class="cards">${cards.map(c => `<div class="card"><div class="cv">${esc(c.value)}</div><div class="cl">${esc(c.label)}</div></div>`).join("")}</div>`
  : "";

const tableHtml = ({ heading, headers, rows, rightCols = [] }) => {
  const rightSet = new Set(rightCols);
  const head = headers.map(h => `<th class="${rightSet.has(h) ? "r" : ""}">${esc(h)}</th>`).join("");
  const body = rows && rows.length
    ? rows.map(r => `<tr>${r.map((cell, ci) => {
        const h = headers[ci];
        const right = rightSet.has(h);
        const isNum = right || /^(PKR|Rs)/.test(String(cell));
        const sc = STATUS_COLOR[cell];
        const cls = `${right ? "r " : ""}${isNum ? "num" : ""}`.trim();
        const style = sc ? ` style="color:${sc};font-weight:700"` : "";
        return `<td class="${cls}"${style}>${esc(cell)}</td>`;
      }).join("")}</tr>`).join("")
    : `<tr><td class="empty" colspan="${headers.length}">No data.</td></tr>`;
  return `${heading ? `<div class="sec-h">${esc(heading)}</div>` : ""}<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
};

const kvHtml = ({ heading, pairs }) => {
  if (!pairs || pairs.length === 0) return "";
  return `${heading ? `<div class="sec-h">${esc(heading)}</div>` : ""}<div class="kv">${pairs.map(([k, v]) => `<div class="kv-row"><span class="kv-k">${esc(k)}</span><span class="kv-v">${esc(v)}</span></div>`).join("")}</div>`;
};

const sectionsHtml = (sections = []) => sections.map(sec => sec.pairs ? kvHtml(sec) : tableHtml(sec)).join("");

const scopeHtml = (scopeMeta = []) => scopeMeta && scopeMeta.length
  ? `<div class="scope">${scopeMeta.map(([k, v]) => `${esc(k)}: <b>${esc(v)}</b>`).join(" &nbsp;·&nbsp; ")}</div>`
  : "";

// ── Full printable document ───────────────────────────────────────────────────
const buildDoc = ({ title, campus, scopeMeta, summaryCards, sections }) => {
  const logo = `${window.location.origin}${SCHOOL_LOGO}`;
  return `<!DOCTYPE html><html><head><meta charset="utf-8" />
<title>${esc(title)} — ${esc(SCHOOL_NAME)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600;700&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Inter','Segoe UI',sans-serif;color:#1a1d23;padding:28px 30px;font-size:12px;background:#fff}
  .lh{display:flex;align-items:center;gap:18px;border-bottom:3px solid #0f1d36;padding-bottom:14px;position:relative}
  .lh::after{content:"";position:absolute;left:0;right:0;bottom:-3px;height:3px;background:#8a6c1f}
  .lh img{height:60px;width:auto;object-fit:contain}
  .lh .school{font-family:'Playfair Display',Georgia,serif;font-size:26px;font-weight:800;color:#0f1d36;line-height:1.1}
  .lh .campus{font-size:12px;font-weight:700;color:#8a6c1f;text-transform:uppercase;letter-spacing:2px;margin-top:3px}
  .lh .sub{font-size:11px;color:#5a5e6a;margin-top:3px}
  .meta{display:flex;justify-content:space-between;align-items:baseline;margin:16px 0 6px}
  .meta .title{font-family:'Playfair Display',Georgia,serif;font-size:16px;font-weight:700;color:#1a1d23}
  .meta .gen{font-size:11px;color:#5a5e6a}
  .scope{font-size:11.5px;color:#5a5e6a;margin-bottom:14px;padding:8px 14px;background:#f8f6f0;border-radius:6px;border:1px solid #e8e2d5}
  .scope b{color:#1a1d23}
  .cards{display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap}
  .card{flex:1;min-width:130px;border:1px solid #ddd3bd;border-top:3px solid #0f1d36;border-radius:8px;padding:10px 12px;background:#fbf8f2}
  .card .cv{font-family:'IBM Plex Mono',monospace;font-variant-numeric:tabular-nums;font-size:15px;font-weight:700;color:#1a1d23}
  .card .cl{font-size:10px;color:#5a5e6a;text-transform:uppercase;letter-spacing:0.5px;margin-top:3px}
  .sec-h{font-family:'Playfair Display',Georgia,serif;font-size:13px;font-weight:700;color:#0f1d36;margin:18px 0 8px;padding-bottom:4px;border-bottom:1px solid #ddd3bd}
  table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:6px}
  thead th{background:#0f1d36;color:#fff;text-align:left;padding:9px 10px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;border-bottom:3px solid #8a6c1f}
  thead th.r{text-align:right}
  tbody td{padding:8px 10px;border-bottom:1px solid #e2ddcd;color:#1a1d23}
  tbody td.r,tbody td.num{text-align:right}
  tbody td.num{font-family:'IBM Plex Mono',monospace;font-variant-numeric:tabular-nums}
  tbody tr:nth-child(even) td{background:#fbf8f2}
  tbody tr:hover td{background:#e8f0fe}
  td.empty{text-align:center;padding:24px;color:#8e92a0;font-style:italic}
  .kv{display:grid;grid-template-columns:1fr 1fr;gap:0 26px;margin-bottom:6px}
  .kv-row{display:flex;justify-content:space-between;gap:14px;padding:6px 0;border-bottom:1px dashed #ddd3bd}
  .kv-row:last-child{border-bottom:none}
  .kv-k{font-size:10.5px;font-weight:700;color:#5a5e6a;text-transform:uppercase;letter-spacing:0.4px}
  .kv-v{font-size:12px;font-weight:600;color:#1a1d23;text-align:right}
  .foot{margin-top:26px;padding-top:14px;border-top:2px solid #0f1d36;display:flex;justify-content:space-between;align-items:flex-end}
  .sign{font-size:11px;color:#5a5e6a}
  .sign .line{border-top:1px solid #1a1d23;width:170px;margin-top:30px;padding-top:4px;text-align:center;font-size:10px;color:#5a5e6a}
  .stamp{font-size:9.5px;color:#8e92a0;text-align:right;line-height:1.6}
  @media print{
    @page{size:A4 portrait;margin:14mm 12mm; @bottom-right{content:"Page " counter(page) " of " counter(pages);font-size:9px;color:#8e92a0}}
    body{-webkit-print-color-adjust:exact;print-color-adjust:exact;padding:0}
    thead{display:table-header-group}
    tr{page-break-inside:avoid}
    .lh,.cards,.foot,.kv-row{page-break-inside:avoid}
    .foot{margin-top:18px}
  }
  @media screen{
    body{max-width:1100px;margin:0 auto;padding:28px 30px;box-shadow:0 2px 20px rgba(0,0,0,0.08)}
  }
</style>
</head>
<body>
  <div class="lh">
    <img src="${logo}" alt="School logo" onerror="this.style.display='none'" />
    <div>
      <div class="school">${esc(SCHOOL_NAME)}</div>
      ${campus ? `<div class="campus">${esc(campus)}</div>` : ""}
      <div class="sub">${esc(title)}</div>
    </div>
  </div>
  <div class="meta">
    <div class="title">${esc(title)}</div>
    <div class="gen">Generated on: ${esc(timestamp())}</div>
  </div>
  ${scopeHtml(scopeMeta)}
  ${cardsHtml(summaryCards)}
  ${sectionsHtml(sections)}
  <div class="foot">
    <div class="sign"><div class="line">Prepared by</div></div>
    <div class="sign"><div class="line">Verified by (Principal)</div></div>
    <div class="stamp">Computer-generated report<br/>${esc(SCHOOL_NAME)}${campus ? ` — ${esc(campus)}` : ""}</div>
  </div>
  <script>
    (function(){ function go(){ window.focus(); window.print(); }
      if (document.readyState === 'complete') setTimeout(go, 350);
      else window.addEventListener('load', function(){ setTimeout(go, 350); }); })();
  </script>
</body></html>`;
};

// ── Public: print ─────────────────────────────────────────────────────────────
export const printReport = ({ title, campus = "", scopeMeta = [], summaryCards = [], sections = [] }) => {
  const win = window.open("", "_blank");
  if (!win) { alertDialog("Please allow pop-ups for this site to print the report.", { title: "Pop-up Blocked" }); return; }
  win.document.write(buildDoc({ title, campus, scopeMeta, summaryCards, sections }));
  win.document.close();
};

// ── Public: CSV ───────────────────────────────────────────────────────────────
export const exportCsv = ({ filename, title, campus = "", scopeMeta = [], sections = [] }) => {
  const rows = [];
  const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

  rows.push(["School", SCHOOL_NAME]);
  if (campus) rows.push(["Campus", campus]);
  rows.push(["Report", title]);
  rows.push(["Generated On", timestamp()]);
  scopeMeta.forEach(([k, v]) => rows.push([k, v]));

  sections.forEach(sec => {
    rows.push([]);
    if (sec.heading) rows.push([sec.heading]);
    if (sec.pairs) {
      sec.pairs.forEach(([k, v]) => rows.push([k, v]));
    } else if (sec.rows && sec.rows.length > 0) {
      rows.push(sec.headers);
      sec.rows.forEach(r => rows.push(r));
    } else {
      rows.push(sec.headers);
      rows.push(["No data available"]);
    }
  });

  const csv = rows.map(r => r.map(cell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};